import React, { createContext, useReducer } from "react";
import {
  attempCourseEnrollment,
  attempCourseUnenrollment,
  attempDownloadSlides,
  retrieveCourseInfo,
  retrieveFollowedCourses,
  retrieveLectureInfo,
} from "../api/student";
import StudentReducer, {
  SELECT_LECTURE,
  SET_COURSE,
  UPDATE_COURSES,
  DELETE_COURSE,
  SET_LECTURE,
  STUDENT_FETCH_START,
  STUDENT_FETCH_SUCCESS,
  STUDENT_FETCH_FAILURE,
} from "./StudentReducer";

//Initial state
const initialState = {
  courses: null, //The list of courses followed by the current user
  currentLecture: null, //The current lecture opened by the user
  loading: false,
  error: null,
};

//Create context
export const StudentContext = createContext(initialState);

//Provider component
export const StudentProvider = ({ children }) => {
  const [state, dispatch] = useReducer(StudentReducer, initialState);

  /**
   * Retrieves the list of courses followed by the current user and updates the state
   *
   * @returns {Promise<Array<Course>>} - the list of courses followed by the current user
   */
  const getFollowedCourses = async () => {
    dispatch({ type: STUDENT_FETCH_START });
    try {
      const courses = await retrieveFollowedCourses();
      dispatch({ type: UPDATE_COURSES, payload: courses });
      return courses;
    } catch (err) {
      dispatch({ type: STUDENT_FETCH_FAILURE, payload: err });
    }
  };

  /**
   * Retrieves information about a course and updates the state
   *
   * @param {string} uuid - the uuid of the course
   * @returns {Promise<Course>} - the course info
   */
  const getCourseInfo = async (uuid) => {
    dispatch({ type: STUDENT_FETCH_START });
    try {
      const info = await retrieveCourseInfo(uuid);
      dispatch({ type: SET_COURSE, payload: info });
      return info;
    } catch (err) {
      dispatch({ type: STUDENT_FETCH_FAILURE, payload: err });
    }
  };

  /**
   * Retrieves information about a lecture and updates the state
   *
   * @param {string} uuid - the uuid of the course
   * @returns {Promise<Lecture>} - the course info
   */
  const getLectureInfo = async (uuid) => {
    dispatch({ type: STUDENT_FETCH_START });
    try {
      const info = await retrieveLectureInfo(uuid);
      dispatch({ type: SET_LECTURE, payload: info });
      return info;
    } catch (err) {
      dispatch({ type: STUDENT_FETCH_FAILURE, payload: err });
    }
  };

  /**
   * Retrieves the video seconds passed from the begin of the youtube live video stream
   *
   * @param {string} uuid - the uuid of the course
   * @returns {Promise<integer>} - the video seconds
   */
  const getLiveCurrentVideoTime = async (uuid) => {
    dispatch({ type: STUDENT_FETCH_START });
    try {
      const info = await retrieveLectureInfo(uuid);
      dispatch({ type: SET_LECTURE, payload: info });
      return info.videoTime;
    } catch (err) {
      dispatch({ type: STUDENT_FETCH_FAILURE, payload: err });
    }
  };

  /**
   * Retrieves lecture info and updates the state
   *
   * @param {string} uuid - the lecture uuid
   * @returns {Promise<Lecture>} - the lecture info
   */
  const selectLecture = async (uuid) => {
    dispatch({ type: STUDENT_FETCH_START });
    try {
      const lecture = await retrieveLectureInfo(uuid);
      dispatch({
        type: SELECT_LECTURE,
        payload: lecture,
      });
      return lecture;
    } catch (err) {
      dispatch({ type: STUDENT_FETCH_FAILURE, payload: err });
    }
  };

  /**
   * Enrolls the current user in a course and updates the state
   *
   * @param {string} uuid - the uuid of the course
   */
  const enrollInCourse = async (uuid) => {
    dispatch({ type: STUDENT_FETCH_START });
    try {
      await attempCourseEnrollment(uuid);
      return getCourseInfo(uuid);
    } catch (err) {
      dispatch({ type: STUDENT_FETCH_FAILURE, payload: err });
    }
  };

  /**
   * Unenrolls the current user from a course and updates the state
   *
   * @param {string} uuid - the uuid of the course
   */
  const unenrollFromCourse = async (uuid) => {
    dispatch({ type: STUDENT_FETCH_START });
    try {
      await attempCourseUnenrollment(uuid);
      dispatch({ type: DELETE_COURSE, payload: uuid });
    } catch (err) {
      dispatch({ type: STUDENT_FETCH_FAILURE, payload: err });
    }
  };

  /**
   * Download slides from a lecture
   *
   */
  const downloadSlides = async (uuid) => {
    dispatch({ type: STUDENT_FETCH_START });
    try {
      const data = await attempDownloadSlides(uuid);
      dispatch({ type: STUDENT_FETCH_SUCCESS });
      return data;
    } catch (err) {
      dispatch({ type: STUDENT_FETCH_FAILURE, payload: err });
    }
  };

  const getSlidesURL = (lectureUUID) => {
    const slidesURL =
      process.env.NODE_ENV === "production"
        ? `${process.env.REACT_APP_SLIDES_PROD_URL}${lectureUUID}/slides`
        : `${process.env.REACT_APP_SLIDES_DEV_URL}${lectureUUID}/slides`;
    return slidesURL;
  };

  return (
    <StudentContext.Provider
      value={{
        courses: state.courses,
        currentLecture: state.currentLecture,
        loading: state.loading,
        error: state.error,
        getFollowedCourses,
        getCourseInfo,
        getLectureInfo,
        getLiveCurrentVideoTime,
        selectLecture,
        enrollInCourse,
        unenrollFromCourse,
        downloadSlides,
        getSlidesURL,
      }}
    >
      {children}
    </StudentContext.Provider>
  );
};
